import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/apiClient";
import { formatMoney } from "@/lib/format";

const adminCustomersApi = {
  list: (params = {}) => api.get("/admin/customers", { params }).then((r) => r.data),
  detail: (email) =>
    api.get(`/admin/customers/by-email/${encodeURIComponent(email)}`).then((r) => r.data),
};

export default function AdminCustomers() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const params = { page, page_size: 25 };
      if (q) params.q = q;
      setData(await adminCustomersApi.list(params));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [page]);

  const openDetail = async (email) => {
    setSelected({ email, loading: true });
    try {
      setSelected(await adminCustomersApi.detail(email));
    } catch (e) {
      setSelected({ email, error: e.response?.data?.error?.message || "Failed" });
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-customers-root">
      <header className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin › Customers</div>
            <div className="text-lg font-semibold">Customer directory</div>
          </div>
          <Button asChild variant="outline"><Link to="/admin">&larr; Dashboard</Link></Button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10 grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">All customers</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <form onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }} className="flex gap-2">
                <Input
                  placeholder="Search by name, email, phone"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="admin-customers-search"
                />
                <Button type="submit" variant="secondary">Search</Button>
              </form>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="p-3 text-left">Customer</th>
                      <th className="p-3 text-right">Orders</th>
                      <th className="p-3 text-right">Spent</th>
                      <th className="p-3 text-left">Last order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((c) => (
                      <tr key={c.email} className="border-t cursor-pointer hover:bg-muted/40"
                          onClick={() => openDetail(c.email)}
                          data-testid={`admin-customer-row-${c.email}`}>
                        <td className="p-3">
                          <div className="font-medium">{c.name || "—"}</div>
                          <div className="text-xs text-muted-foreground">{c.email}</div>
                          <div className="text-xs text-muted-foreground">{c.phone}</div>
                        </td>
                        <td className="p-3 text-right tabular-nums">{c.order_count}</td>
                        <td className="p-3 text-right tabular-nums">
                          {formatMoney({ amount_minor: c.total_spent_minor, currency: c.currency })}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {c.last_order_at && new Date(c.last_order_at).toLocaleString()}
                          {c.last_emirate && <div>{c.last_emirate}</div>}
                        </td>
                      </tr>
                    ))}
                    {data.items.length === 0 && !busy && (
                      <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No customers yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {data.total > 25 && (
                <div className="flex justify-between items-center pt-2">
                  <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                  <div className="text-sm text-muted-foreground">Page {page}</div>
                  <Button variant="outline" disabled={page * 25 >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <aside>
          {!selected && (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
              Click a customer to see their full order history.
            </CardContent></Card>
          )}
          {selected && (
            <Card data-testid="admin-customer-detail">
              <CardHeader>
                <CardTitle className="text-base">{selected.name || selected.email}</CardTitle>
                <div className="text-xs text-muted-foreground">{selected.email}</div>
                {selected.phone && <div className="text-xs text-muted-foreground">{selected.phone}</div>}
              </CardHeader>
              <CardContent className="space-y-3">
                {selected.loading && <div className="text-sm text-muted-foreground">Loading…</div>}
                {selected.error && <div className="text-sm text-destructive">{selected.error}</div>}
                {selected.orders && (
                  <>
                    <div className="text-sm">
                      <div><span className="text-muted-foreground">Total orders:</span> {selected.order_count}</div>
                      <div><span className="text-muted-foreground">Total spent:</span> {formatMoney({ amount_minor: selected.total_spent_minor, currency: selected.currency })}</div>
                    </div>
                    <div className="border-t pt-3 space-y-2">
                      {selected.orders.map((o) => (
                        <Link key={o.id} to={`/admin/orders/${o.id}`} className="block text-sm hover:bg-muted/40 rounded px-2 py-1 -mx-2">
                          <div className="flex justify-between">
                            <div className="font-mono text-xs">{o.order_number}</div>
                            <div className="font-medium">{formatMoney({ amount_minor: o.grand_total_minor, currency: selected.currency })}</div>
                          </div>
                          <div className="text-xs text-muted-foreground">{o.status} · {o.emirate} · {new Date(o.created_at).toLocaleDateString()}</div>
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </aside>
      </main>
    </div>
  );
}
